// Tiny fetch wrapper. Cookies are same-origin (sent automatically).

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

// Called whenever the server answers 401, so the app can drop its auth cache
// and bounce to login from one place instead of guessing on every view.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.();
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// --- Types ---
export type Suggestion = {
  weight_kg: number;
  reps: number;
  reason: 'first-time' | 'progress-reps' | 'progress-weight';
};

export type LastSet = {
  set_number: number;
  weight_kg: number;
  reps: number;
};

export type WorkoutExercise = {
  id: number;
  name: string;
  muscle_group: string;
  position: number;
  rep_min: number;
  rep_max: number;
  default_sets: number;
  rest_seconds: number;
  weight_step_kg: number;
  uses_barbell: boolean;
  bar_weight_kg: number;
  last_sets: LastSet[] | null;
  suggestion: Suggestion;
};

export type WorkoutDetail = {
  workout: { id: number; code: string; name: string };
  exercises: WorkoutExercise[];
};

export type WorkoutSummary = { id: number; code: string; name: string };

export type SessionRow = {
  id: number;
  workout_code: string;
  workout_name: string;
  started_at: string;
  finished_at: string | null;
  notes: string | null;
  set_count: number;
};

export type ActiveSession = {
  id: number;
  workout_code: string;
  workout_name: string;
  started_at: string;
  set_count: number;
};

// A set already persisted on the server, used to rehydrate an in-progress
// session when resuming or reloading.
export type SessionSetRow = {
  client_uuid: string;
  exercise_id: number;
  set_number: number;
  weight_kg: number;
  reps: number;
};

export type SessionDetail = {
  session: {
    id: number;
    workout_code: string;
    workout_name: string;
    started_at: string;
    finished_at: string;
    notes: string | null;
  };
  sets: {
    id: number;
    exercise_id: number;
    exercise_name: string;
    set_number: number;
    weight_kg: number;
    reps: number;
    rpe: number | null;
    logged_at: string;
  }[];
};

export type PrRow = {
  exercise_id: number;
  name: string;
  muscle_group: string;
  weight_kg: number;
  reps: number;
  achieved_at: string;
};

export type EvolutionPoint = { started_at: string; top_weight: number; top_volume: number };
